import {
  useDeleteCertificateLogo,
  useGetCertificateLogo,
  useUploadCertificateLogo,
} from "@/api/pdf";
import { toast } from "@/hooks/use-toast";
import { resampleFileToMaxDimension } from "@/lib/canvas";
import { cn } from "@/lib/utils";
import { Loader2, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** Limite appliquée par l'API (`validateCertificateLogo`). */
const MAX_CERTIFICATE_LOGO_DIMENSION = 600;

const CertificateLogoUpload = () => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, isLoading: isLoadingLogo } = useGetCertificateLogo();
  const { mutate: uploadLogo, isPending: isUploading } =
    useUploadCertificateLogo();
  const { mutate: deleteLogo, isPending: isDeleting } =
    useDeleteCertificateLogo();
  const [isResampling, setIsResampling] = useState(false);

  const isProcessing = isUploading || isDeleting || isResampling;
  const hasLogo = Boolean(data?.logoUrl);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;

    // L'API rejette au-delà de 600 px et hors JPEG : on redimensionne et on
    // ré-encode à la volée plutôt que de renvoyer l'utilisateur vers un outil
    // externe. Le fond blanc remplace la transparence d'un PNG/WebP, que le
    // JPEG ne sait pas porter (sinon les zones transparentes ressortent en
    // noir) — et le certificat s'imprime sur une page blanche.
    let fileToUpload = file;
    try {
      setIsResampling(true);
      const resampled = await resampleFileToMaxDimension(
        file,
        MAX_CERTIFICATE_LOGO_DIMENSION,
        { mimeType: "image/jpeg", background: "#ffffff" },
      );
      fileToUpload = resampled.file;
      if (resampled.resized) {
        toast({
          title: t("certificateLogo.autoResizedTitle", {
            defaultValue:
              "Logo redimensionné (certificateLogo.autoResizedTitle)",
          }),
          description: t("certificateLogo.autoResized", {
            width: resampled.width,
            height: resampled.height,
            defaultValue: `Image ramenée à ${resampled.width} × ${resampled.height} px (certificateLogo.autoResized)`,
          }),
          duration: 3000,
        });
      }
    } catch {
      // Image illisible par le canvas : on laisse l'API trancher.
      fileToUpload = file;
    } finally {
      setIsResampling(false);
    }

    uploadLogo(fileToUpload, {
      onError: () => {
        toast({
          title: t("settingsErrorToastTitle"),
          description: t("settingsErrorToastDescription"),
          duration: 3000,
          variant: "destructive",
        });
      },
    });
  };

  const handleDelete = () => {
    deleteLogo(undefined, {
      onError: () => {
        toast({
          title: t("settingsErrorToastTitle"),
          description: t("settingsErrorToastDescription"),
          duration: 3000,
          variant: "destructive",
        });
      },
    });
  };

  if (isLoadingLogo) {
    return (
      <div className="flex items-center justify-center w-full min-h-[140px] border border-slate-300 rounded-xl">
        <Loader2 className="w-6 h-6 text-gray-500 animate-spin" />
      </div>
    );
  }

  // Logo loaded — show preview with hover delete overlay
  if (hasLogo && data?.logoUrl) {
    return (
      <div className="relative group w-full min-h-[140px] border border-slate-300 rounded-xl overflow-hidden">
        <img
          src={data.logoUrl}
          alt="Logo"
          className="w-full h-full object-contain p-4"
        />
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity",
            isProcessing && "opacity-100",
          )}
        >
          {isProcessing ? (
            <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
          ) : (
            <button
              type="button"
              onClick={handleDelete}
              className="flex items-center gap-2 bg-transparent text-red-500 hover:text-red-600 text-base font-semibold transition-colors"
            >
              <Trash2 className="w-5 h-5" />
              {t("delete")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <label
      className={cn(
        "relative flex flex-col items-center justify-center w-full gap-3 py-6 px-4 border border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors text-center",
        isProcessing && "pointer-events-none opacity-50",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
      <Upload className="w-8 h-8 text-slate-400" />
      <div className="space-y-1">
        <p className="text-sm text-slate-700">
          <span className="text-blue-500 underline">{t("certificateLogo.clickCta")}</span>
          {" "}{t("certificateLogo.dragCta")}
        </p>
        <p className="text-xs text-slate-400">
          {t("certificateLogo.acceptedFormats")}
        </p>
      </div>
    </label>
  );
};

export default CertificateLogoUpload;
